<script setup>
import { Link } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { Bell, Users, User, Video, MessageSquare, Trophy, Calendar } from 'lucide-vue-next'
import FeatureCheckItem from './FeatureCheckItem.vue'
import { tutors } from '@/routes/index'
</script>

<template>
  <section class="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20 min-h-[700px]">
    <div class="max-w-7xl mx-auto px-4 md:px-8">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <!-- Left Content -->
        <div class="lg:col-span-6">
          <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Weekly tutoring with real access and ongoing support
          </h1>
          <p class="hidden md:block text-md md:text-xl text-gray-600 mb-8 leading-relaxed">
            Bindle gives students continuous access to top-quality tutors —
            live weekly sessions, recorded explanations, messaging, and
            progress tracking — all in one flexible membership.
          </p>
          <p class="md:hidden text-md md:text-xl text-gray-600 mb-8 leading-relaxed">
            Continuous access to top-quality tutors — live sessions, recordings, messaging, and progress tracking.
          </p>
          <div class="flex mb-8">
            <Link :href="tutors()"
                  class="w-full text-center md:w-auto md:text-left px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all hover:shadow-lg"
            >
                Find a Tutor Plan
            </Link>
          </div>
          <div class="flex flex-wrap items-center gap-6 text-sm text-gray-600">
            <FeatureCheckItem text="GCSE & A-level specialists" />
            <FeatureCheckItem text="Weekly access" />
            <FeatureCheckItem text="Cancel anytime" />
          </div>
        </div>

        <!-- Right Content - Dashboard Preview -->
        <div class="lg:col-span-6">
          <div class="relative">
            <div class="bg-white rounded-2xl shadow-2xl p-4 border-2 border-gray-100">
              <!-- Dashboard Header -->
              <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                <div class="flex items-center space-x-2">
                  <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg" alt="Student" class="w-9 h-9 rounded-full border-2 border-blue-100">
                  <div>
                    <p class="font-bold text-gray-900 text-sm">My Dashboard</p>
                    <p class="text-xs text-gray-500">Welcome back, Sarah</p>
                  </div>
                </div>
                <Bell class="text-gray-400 w-4 h-4" />
              </div>

              <!-- Weekly Schedule -->
              <div class="mb-3">
                <div class="flex items-center justify-between mb-2">
                  <p class="text-sm font-bold text-gray-900">Weekly Schedule</p>
                  <span class="text-xs text-gray-500">This week</span>
                </div>
                <div class="space-y-2">
                  <!-- Group Session -->
                  <div class="bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg p-2.5 border border-purple-300">
                    <div class="flex items-center justify-between mb-1">
                      <div class="flex items-center space-x-2">
                        <Users class="text-purple-600 w-4 h-4" />
                        <p class="text-sm font-semibold text-purple-900">Group Session</p>
                      </div>
                      <span class="text-xs bg-purple-300/60 text-purple-900 px-2 py-0.5 rounded-full">Tomorrow</span>
                    </div>
                    <p class="text-purple-900 font-bold text-sm mb-0.5">GCSE Chemistry</p>
                    <div class="flex items-center justify-between">
                      <p class="text-purple-700 text-xs">Dr. Emma Clarke - 3:30 PM</p>
                      <div class="flex -space-x-1">
                        <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg" class="w-4 h-4 rounded-full border border-white" alt="Student">
                        <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg" class="w-4 h-4 rounded-full border border-white" alt="Student">
                        <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-8.jpg" class="w-4 h-4 rounded-full border border-white" alt="Student">
                        <span class="w-4 h-4 rounded-full border border-white bg-purple-300 flex items-center justify-center text-[10px] text-purple-900">+2</span>
                      </div>
                    </div>
                  </div>

                  <!-- 1:1 Session -->
                  <div class="bg-gradient-to-r from-green-100 to-green-200 rounded-lg p-2.5 border border-green-300">
                    <div class="flex items-center justify-between mb-1">
                      <div class="flex items-center space-x-2">
                        <User class="text-green-600 w-4 h-4" />
                        <p class="text-sm font-semibold text-green-900">1:1 Session</p>
                      </div>
                      <span class="text-xs bg-green-300/60 text-green-900 px-2 py-0.5 rounded-full">Wed 4PM</span>
                    </div>
                    <p class="text-green-900 font-bold text-sm">A-Level Maths</p>
                    <p class="text-green-700 text-xs">Prof. James Wilson</p>
                  </div>
                </div>
              </div>

              <!-- My Tutors -->
              <div class="mb-3">
                <p class="text-sm font-bold text-gray-900 mb-2">My Tutors</p>
                <div class="flex items-center gap-2">
                  <div class="relative">
                    <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg" alt="Dr. Emma Clarke" class="w-10 h-10 rounded-full border-2 border-purple-300">
                    <div class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-purple-400 rounded-full flex items-center justify-center border border-white">
                      <Users class="text-white w-2 h-2" />
                    </div>
                  </div>
                  <div class="relative">
                    <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-4.jpg" alt="Prof. James Wilson" class="w-10 h-10 rounded-full border-2 border-green-300">
                    <div class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full flex items-center justify-center border border-white">
                      <User class="text-white w-2 h-2" />
                    </div>
                  </div>
                  <div class="relative">
                    <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-9.jpg" alt="Sarah Martinez" class="w-10 h-10 rounded-full border-2 border-blue-300">
                    <div class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-blue-400 rounded-full flex items-center justify-center border border-white">
                      <Video class="text-white w-2 h-2" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stats Grid -->
              <div class="grid grid-cols-4 gap-2">
                <div class="bg-blue-50 rounded-lg p-2 text-center">
                  <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Calendar class="text-blue-600 w-3 h-3" />
                  </div>
                  <p class="text-sm font-bold text-gray-900">12</p>
                  <p class="text-xs text-gray-600">Sessions</p>
                </div>
                <div class="bg-green-50 rounded-lg p-2 text-center">
                  <div class="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Video class="text-emerald-600 w-3 h-3" />
                  </div>
                  <p class="text-sm font-bold text-gray-900">47</p>
                  <p class="text-xs text-gray-600">Videos</p>
                </div>
                <div class="bg-purple-50 rounded-lg p-2 text-center">
                  <div class="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-1">
                    <MessageSquare class="text-purple-600 w-3 h-3" />
                  </div>
                  <p class="text-sm font-bold text-gray-900">2</p>
                  <p class="text-xs text-gray-600">Messages</p>
                </div>
                <div class="bg-orange-50 rounded-lg p-2 text-center">
                  <div class="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Trophy class="text-orange-600 w-3 h-3" />
                  </div>
                  <p class="text-sm font-bold text-gray-900">85%</p>
                  <p class="text-xs text-gray-600">Progress</p>
                </div>
              </div>

              <!-- Weekly Streak -->
              <div class="mt-3 pt-2 border-t border-gray-200">
                <div class="flex items-center justify-between mb-1">
                  <p class="text-xs text-gray-600">Weekly streak</p>
                  <p class="text-xs font-bold text-gray-900">4 weeks</p>
                </div>
                <div class="w-full h-1.5 bg-gray-200 rounded-full">
                  <div class="w-4/5 h-1.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                </div>
              </div>
            </div>

            <!-- Decorative blurs -->
            <div class="absolute -top-4 -right-4 w-24 h-24 bg-purple-200 rounded-full opacity-50 blur-2xl"></div>
            <div class="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-200 rounded-full opacity-50 blur-2xl"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
